import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  House, 
  Package, 
  MagnifyingGlass, 
  FileText, 
  Gear, 
  SignOut,
  Car,
  Storefront,
  Truck,
  Fire,
  Users,
  ChartLine,
  List,
  X
} from '@phosphor-icons/react';
import { useAuth } from '../../context/AuthContext';

const AdminLayout = ({ children, title }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const menuItems = [
    { href: '/admin', label: 'Dashboard', icon: House },
    { href: '/admin/pieces', label: 'Catalogue Pi\u00e8ces', icon: Package },
    { href: '/admin/analyses', label: 'Analyses', icon: MagnifyingGlass },
    { href: '/admin/demandes', label: 'Demandes Devis', icon: FileText },
    { href: '/admin/audience', label: 'Audience', icon: ChartLine },
    { href: '/admin/parametres', label: 'Param\u00e8tres Analyse', icon: Gear },
    { href: '/admin/pros', label: 'Comptes Pro', icon: Users },
    { type: 'divider' },
    { href: '/admin/pro', label: 'Espace Pro', icon: Fire },
    { href: '/admin/pro/stock', label: 'Stock V\u00e9hicules', icon: Truck },
    { href: '/admin/pro/reception', label: 'R\u00e9ceptionner', icon: Storefront },
  ];

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const closeSidebar = () => setSidebarOpen(false);

  const sidebarContent = (
    <>
      <div className="p-5 border-b border-white/10 flex items-center justify-between">
        <Link to="/admin" className="flex items-center gap-3" onClick={closeSidebar}>
          <div className="w-9 h-9 bg-[#E63946] flex items-center justify-center">
            <Car size={20} weight="bold" className="text-white" />
          </div>
          <div>
            <span className="font-['Outfit'] text-base font-bold tracking-tight text-white block">ECOCASSE</span>
            <span className="text-[9px] uppercase tracking-[0.2em] text-white/40">Administration</span>
          </div>
        </Link>
        <button onClick={closeSidebar} className="lg:hidden text-white/60 hover:text-white p-1" data-testid="close-sidebar">
          <X size={22} />
        </button>
      </div>

      <nav className="flex-1 p-3 overflow-y-auto">
        <ul className="space-y-0.5">
          {menuItems.map((item, idx) => {
            if (item.type === 'divider') return <li key={`div-${idx}`} className="my-2 border-t border-white/10" />;
            const Icon = item.icon;
            const isActive = location.pathname === item.href;
            return (
              <li key={item.href}>
                <Link
                  to={item.href}
                  onClick={closeSidebar}
                  className={`flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-colors rounded ${
                    isActive
                      ? 'bg-[#E63946]/10 text-[#E63946] border-l-2 border-[#E63946]'
                      : 'text-white/70 hover:text-white hover:bg-white/5'
                  }`}
                  data-testid={`admin-nav-${item.label.toLowerCase().replace(/[ èé]/g, '-')}`}
                >
                  <Icon size={18} weight={isActive ? 'fill' : 'regular'} />
                  {item.label}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      <div className="p-3 border-t border-white/10">
        <div className="px-3 py-2 mb-1">
          <p className="text-[10px] text-white/40 uppercase tracking-wider">Connect&eacute;</p>
          <p className="text-sm text-white font-medium truncate">{user?.name}</p>
        </div>
        <button
          onClick={handleLogout}
          className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-white/70 hover:text-[#E63946] hover:bg-white/5 w-full transition-colors rounded"
          data-testid="admin-logout-btn"
        >
          <SignOut size={18} />
          D&eacute;connexion
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#F5F5F5] flex">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={closeSidebar} />
      )}

      {/* Sidebar — hidden on mobile, slide-in when open */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-[#1A1A1A] flex flex-col transition-transform duration-200 ease-in-out
        lg:relative lg:translate-x-0 lg:z-auto
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {sidebarContent}
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-w-0">
        <header className="h-14 bg-white border-b border-gray-200 flex items-center px-4 md:px-8 shadow-sm gap-3 flex-shrink-0">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden text-gray-700 p-1.5 hover:bg-gray-100 rounded"
            data-testid="open-sidebar"
          >
            <List size={22} />
          </button>
          <h1 className="font-['Outfit'] text-lg md:text-xl font-bold text-gray-900 truncate">
            {title}
          </h1>
        </header>

        <div className="flex-1 p-4 md:p-6 lg:p-8 overflow-auto">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
